import { useDispatch } from 'react-redux';
import { freezeTime, unfreezeTime } from '../../redux/freezeApi';
import CountdownBar from './CountdownBar';
import './FreezeButton.css';

// Stages an ACTIVE freeze may start in: any stage of a live round, pre-showdown. Mirrors
// the backend's gate ("You can only freeze time during a live round"); it re-validates.
const FREEZABLE_STAGES = ['exchange', 'predeal', 'preflop', 'flop', 'turn', 'river'];

// Circular Freeze/Unfreeze button, rendered INSIDE StagePhaseIndicator as a
// pointer-events: auto island just right of the seven-box row (the container itself stays
// pointer-events: none). Powered by the hero's per-seat freeze time bank; deliberately NOT
// gated on a resolving Power-Up — freezing OVER a pup window (pausing its countdown at the
// remaining time) is a designed use. States:
//   - hidden for spectators (no seat);
//   - "Unfreeze" while the hero is a freezer (always enabled — the way out);
//   - disabled (greyed, .replay-btn:disabled model) outside a live-round stage, while
//     someone ELSE is freezing, or with an empty bank;
//   - "Freeze" otherwise.
// While any freeze is live, a thin CountdownBar under the button drains the freeze window
// (active = the freezer's whole bank; queued = the shared 10s), server-clock-anchored.
const FreezeButton = ({ roomId, stage, mySeat, frozenSeats = [], freezeTimeBank = 0,
                        freezeStartTime = null, freezeDurationMs = null, size = 44 }) => {
  const dispatch = useDispatch();
  if (mySeat == null) return null; // spectators never see it

  const timeFrozen = frozenSeats.length > 0;
  const heroFrozen = frozenSeats.includes(mySeat);
  const disabled = !heroFrozen && (
    !FREEZABLE_STAGES.includes(stage)
    || timeFrozen // frozen by someone else (heroFrozen already excluded above)
    || freezeTimeBank <= 0
  );
  const title = heroFrozen ? 'Resume the table'
    : timeFrozen ? 'Another player is freezing time'
      : !FREEZABLE_STAGES.includes(stage) ? 'You can only freeze time during a live round'
        : freezeTimeBank <= 0 ? 'Your freeze time bank is empty'
          : `Freeze time (${freezeTimeBank}s banked)`;

  // The circle is the stage box-row height now, which is a good deal smaller than the old
  // max(44px, boxHeight*1.6) floor — so the label type has to scale with it or the uppercased
  // word spills well outside the ring. 4.2 keeps ~6 uppercase glyphs inside the content box
  // (diameter minus the 3px border each side); the min() pins it at today's 10px/1px whenever
  // the button is big enough, so nothing changes at the sizes this shipped with.
  const fontPx = Math.max(6, Math.min(10, (size - 6) / 4.2));

  return (
    <div className="freeze-btn-wrap" style={{ width: `${size}px` }}>
      <button
        type="button"
        className="freeze-btn"
        style={{
          width: `${size}px`,
          height: `${size}px`,
          '--freeze-font': `${fontPx.toFixed(2)}px`,
          '--freeze-track': size >= 44 ? '1px' : '0px',
        }}
        disabled={disabled}
        title={title}
        onClick={() => dispatch(heroFrozen ? unfreezeTime({ roomId }) : freezeTime({ roomId }))}
      >
        {heroFrozen ? 'Unfreeze' : 'Freeze'}
      </button>
      {timeFrozen && freezeStartTime != null && (
        <CountdownBar
          key={`freeze-${freezeStartTime}`}
          className="freeze-btn-countdown"
          durationMs={freezeDurationMs || 10000}
          startTime={freezeStartTime}
        />
      )}
    </div>
  );
};

export default FreezeButton;
